import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Truck } from 'lucide-react';
import { qs } from '@/lib/api';
import { usePagedList } from '@/hooks/usePagedList';
import { ListFooter } from '@/features/common/ListFooter';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useIsPhone } from '@/hooks/useIsPhone';
import { formatDate } from '@/lib/dates';
import { PageHeader } from '@/app/DashboardLayout';
import {
  Badge, Button, Card, CardLine, CardRow, Cards, EmptyState, ErrorState, Input, Money,
  SkeletonTable, TBody, TD, TH, THead, TR, Table,
} from '@/features/common/ui';
import { VendorSheet } from './VendorSheet';

interface VendorRow {
  id: string;
  displayName: string;
  companyName: string | null;
  gstin: string | null;
  isActive: boolean;
  isDraft?: boolean;
  spend: { total: string; count: number; lastPaidOn: string | null };
}

/**
 * Who you pay - Customers' other half.
 *
 * A vendor's figure is what has been PAID them: Fint records money that left,
 * not bills owed, so there is no outstanding column here, and the total is the
 * recorded expenses naming them, net of refunds. Opening one shows those
 * expenses with their bills, and the ones not yet marked that look like theirs.
 */
export function VendorsPage() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const isPhone = useIsPhone();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('q') ?? '');
  const [composerOpen, setComposerOpen] = useState(false);

  const query = usePagedList<VendorRow>({
    key: ['vendors', org?.id, search],
    path: (cursor) => `/vendors${qs({ q: search, cursor })}`,
    enabled: !!org?.id,
  });
  const currency = 'INR';

  return (
    <>
      <PageHeader
        title="Vendors"
        subtitle="Everyone you pay, and what you have paid them."
        actions={
          perms.can('vendor', 'create') ? (
            <Button size="sm" onClick={() => setComposerOpen(true)} data-testid="vendor-new">
              <Plus className="size-4" aria-hidden />
              New vendor
            </Button>
          ) : null
        }
      />

      <div className="space-y-4 px-5 pb-8">
        <div className="relative max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input className="pl-8" placeholder="Search by name or GSTIN" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search vendors" />
        </div>

        <Card>
          {query.isError ? (
            <ErrorState error={query.error} onRetry={() => query.refetch()} />
          ) : query.isLoading ? (
            <SkeletonTable rows={5} cols={4} />
          ) : query.items.length ? (
            <>
              {isPhone ? (
                <Cards>
                  {query.items.map((vendor) => (
                    <CardRow key={vendor.id} data-testid="vendor-row">
                      <Link to={`/vendors/${vendor.id}`} className="block">
                        <CardLine
                          title={vendor.displayName}
                          meta={vendor.spend.count ? `${vendor.spend.count} paid · last ${vendor.spend.lastPaidOn ? formatDate(vendor.spend.lastPaidOn) : '—'}` : 'Nothing paid yet'}
                          value={<Money value={vendor.spend.total} currency={currency} />}
                          sub="paid"
                        />
                      </Link>
                    </CardRow>
                  ))}
                </Cards>
              ) : (
                <Table>
                  <THead>
                    <TR>
                      <TH>Name</TH>
                      <TH>GSTIN</TH>
                      <TH numeric>Payments</TH>
                      <TH>Last paid</TH>
                      <TH numeric>Paid in total</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {query.items.map((vendor) => (
                      <TR
                        key={vendor.id}
                        data-testid="vendor-row"
                        className="cursor-pointer hover:bg-accent/40"
                        onClick={() => navigate(`/vendors/${vendor.id}`)}
                      >
                        <TD>
                          <Link to={`/vendors/${vendor.id}`} className="font-medium hover:underline" onClick={(e) => e.stopPropagation()}>
                            {vendor.displayName}
                          </Link>
                          {vendor.isDraft ? <Badge variant="warning" className="ml-2">Draft</Badge> : !vendor.isActive ? <Badge className="ml-2">Inactive</Badge> : null}
                          {vendor.companyName && vendor.companyName !== vendor.displayName ? (
                            <p className="text-xs text-muted-foreground">{vendor.companyName}</p>
                          ) : null}
                        </TD>
                        <TD className="font-mono text-xs text-muted-foreground">{vendor.gstin || '—'}</TD>
                        <TD numeric>{vendor.spend.count}</TD>
                        <TD className="text-muted-foreground">{vendor.spend.lastPaidOn ? formatDate(vendor.spend.lastPaidOn) : '—'}</TD>
                        <TD numeric>
                          <Money value={vendor.spend.total} currency={currency} />
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              )}
              <ListFooter query={query} noun="vendors" />
            </>
          ) : (
            <EmptyState
              icon={Truck}
              title={search ? 'No vendors match that' : 'No vendors yet'}
              description={
                search
                  ? 'Try a different name or GSTIN.'
                  : 'Add the people and businesses you pay - then mark their expenses, and every bill they sent is one click from their name.'
              }
              action={
                !search && perms.can('vendor', 'create') ? (
                  <Button size="sm" onClick={() => setComposerOpen(true)}>
                    <Plus className="size-4" aria-hidden />
                    New vendor
                  </Button>
                ) : null
              }
            />
          )}
        </Card>
      </div>

      {composerOpen ? (
        <VendorSheet
          onClose={() => setComposerOpen(false)}
          onSaved={(vendor) => {
            setComposerOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['vendors', org?.id] });
            navigate(`/vendors/${vendor.id}`);
          }}
        />
      ) : null}
    </>
  );
}
